import {
  CampaignByIdItemDataType,
  MediaPlanDataType,
} from "../../stores/types/campaignsTypes.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCircleCheck,
} from "@fortawesome/pro-light-svg-icons";
import { MediaPlanAudience } from "./components/audience/MediaPlanAudience.tsx";
import {
  funnelDepthSelectData,
  mediaPlanToggleBtnData,
  primaryDimensionSelectData,
} from "./components/data/mediaPlanData.ts";
import { Fragment, useMemo, useState } from "react";
import {
  RevButton,
  RevCard,
  RevLink,
  RevSelect,
  RevToggleButtonGroup,
} from "@revmatics/catalyst";
import { KPIDetailsView } from "./components/view/KPIDetailsView.tsx";
import { MonthlyDetailsView } from "./components/view/MonthlyDetailsView.tsx";
import { formatDateToMonthDayYear } from "../../helpers/functions/formatDate.tsx";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";

type Props = {
  mediaPlanData: MediaPlanDataType;
  campaignById: CampaignByIdItemDataType;
};

export const MediaPlanPage = ({ mediaPlanData, campaignById }: Props) => {
  const [funnelDepth, setFunnelDepth] = useState<string[]>(["awareness"]);
  const [primaryDimension, setPrimaryDimension] = useState<string>("channels");
  const [editView, setEditView] = useState(false);

  const [activeTab, setActiveTab] = useState("kpi_details");

  const monthlyData = useMemo(() => {
    return [
      {
        ...mediaPlanData.monthly_budget_breakdown.all,
        id: 0,
        name: "All",
        sub_groups: null,
      },
      ...mediaPlanData.monthly_budget_breakdown.groups,
    ];
  }, [mediaPlanData.monthly_budget_breakdown]);

  const kpiData = useMemo(() => {
    return Object.keys(mediaPlanData.kpi_details.all).length > 0
      ? [
          {
            kpis: mediaPlanData.kpi_details.all,
            id: 0,
            name: "All",
            sub_groups: null,
          },
          ...mediaPlanData.kpi_details.groups,
        ]
      : mediaPlanData.kpi_details.groups;
  }, [mediaPlanData.kpi_details]);

  const content = useMemo(() => {
    switch (activeTab) {
      case "kpi_details":
        return (
          <KPIDetailsView
            kpi={kpiData}
            kpiFields={mediaPlanData.kpi_details.kpi_fields}
            edit={editView}
          />
        );
      case "monthly":
        return (
          <MonthlyDetailsView
            monthly={monthlyData}
            monthList={mediaPlanData.monthly_budget_breakdown.months}
            edit={editView}
          />
        );
      default:
        return null;
    }
  }, [
    activeTab,
    kpiData,
    mediaPlanData.kpi_details.kpi_fields,
    mediaPlanData.monthly_budget_breakdown.months,
    monthlyData,
    editView,
  ]);

  return (
    <div className="space-y-6">
      <div>
        <h4>Campaign: {campaignById.name}</h4>
        <div className="flex flex-col md:flex-row gap-y-2 gap-x-6 md:items-center mt-1 mb-">
          <div className="flex items-center gap-2">
            <div>
              <p className="typography-body-sm text-monochrome-900">ID:</p>
            </div>
            <RevLink
              pathname=""
              search={`?campaignId=${campaignById.id}/`}
              text={campaignById.id.toString()}
              underline={false}
              iconRight={faArrowUpRightFromSquare}
              iconSize="text-xs"
            />
          </div>
          <p className="typography-body-sm text-monochrome-900">
            Campaign Period:{" "}
            {campaignById.time_frame.start
              ? formatDateToMonthDayYear(campaignById.time_frame.start)
              : "—"}{" "}
            {" — "}
            {campaignById.time_frame.end
              ? formatDateToMonthDayYear(campaignById.time_frame.end)
              : "—"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <RevCard
            padding="py-7.5 px-4"
            className="grid grid-cols-1 lg:grid-cols-[132px_1fr] gap-y-2 xl:gap-x-4"
          >
            <div className="space-y-2 lg:border-r border-monochrome-100">
              <p className="typography-body-sm text-monochrome-500">
                Projected Spend
              </p>
              <h4>$XX.XXX</h4>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-full space-y-2">
                <div className="grid grid-cols-[106px_1fr] gap-x-2 items-end">
                  <div className="flex items-end justify-end gap-1">
                    <p className="typography-body-sm leading-none text-monochrome-500 text-end">
                      Actual
                    </p>
                    <h6 className="leading-none">3.2x</h6>
                  </div>
                  <div className="bg-purple-500 w-[25%] h-[9.5px]"></div>
                </div>
                <div className="grid grid-cols-[106px_1fr] gap-x-2">
                  <div className="flex items-end justify-end gap-1">
                    <p className="typography-body-sm leading-none text-monochrome-500 text-end">
                      Projected
                    </p>
                    <h6 className="leading-none">3.2x</h6>
                  </div>
                  <div className="bg-primary-500 w-full h-[9.5px]"></div>
                </div>
              </div>
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="text-green-500 text-2xl"
              />
            </div>
          </RevCard>
          <RevCard
            padding="py-7.5 px-4"
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <div className="space-y-2">
              <p className="typography-body-sm text-monochrome-500">Spend</p>
              <h4>$XX.XXX</h4>
            </div>
            <div className="space-y-2">
              <p className="typography-body-sm text-monochrome-500">
                Projected Spend
              </p>
              <h4>$XX.XXX</h4>
            </div>
            <div className="space-y-2">
              <p className="typography-body-sm text-monochrome-500">Budget</p>
              <h4>$XX.XXX</h4>
            </div>
          </RevCard>
        </div>
        <MediaPlanAudience />
      </div>
      <div className="flex justify-between items-end gap-2">
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-y-1">
            <span className="typography-body-xs text-monochrome-900">View</span>
            <RevToggleButtonGroup
              data={mediaPlanToggleBtnData}
              idPage="discover-communities"
              selected={activeTab}
              handleSelect={setActiveTab}
            />
          </div>
          <div className="flex flex-col gap-y-1 w-[172px] overflow-hidden">
            <span className="typography-body-xs text-monochrome-900">
              Funnel Depth
            </span>
            <RevSelect
              name="funnelDepth"
              label=""
              options={funnelDepthSelectData}
              onChange={(value) => {
                const valueType = value as typeof funnelDepth;
                setFunnelDepth(valueType);
              }}
              multiple
              value={funnelDepth}
            />
          </div>
          <div className="flex flex-col gap-y-1 min-w-[172px]">
            <span className="typography-body-xs text-monochrome-900">
              Primary Dimension
            </span>
            <RevSelect
              name="primaryDimension"
              label=""
              options={primaryDimensionSelectData}
              onChange={(value) => {
                setPrimaryDimension(value?.toString() || "");
              }}
              value={primaryDimension}
            />
          </div>
        </div>

        {editView ? (
          <div className="flex gap-3">
            <RevButton
              text="Cancel"
              handleClick={() => setEditView(false)}
              buttonSize="medium"
              variant="secondary"
            />
            <RevButton
              text="Save"
              handleClick={() => setEditView(false)}
              buttonSize="medium"
              variant="primary"
            />
          </div>
        ) : (
          <Menu as="div" className="relative">
            {({ open }) => (
              <div>
                <MenuButton
                  as={RevButton}
                  icon={faEllipsisVertical}
                  variant="secondary"
                  className={`${open ? "!bg-primary-50" : "bg-white focus:!bg-primary-50"}`}
                />
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems className="absolute right-0 mt-2 w-48 origin-top-left rounded-md bg-white shadow-elevation-2 focus:outline-none z-10">
                    <div className="py-2">
                      <MenuItem>
                        <button
                          type="button"
                          onClick={() => setEditView(true)}
                          className={`flex w-full items-center justify-between px-4 py-2 typography-body-sm text-monochrome-900 ${editView ? "bg-primary-50" : "hover:bg-monochrome-50"}`}
                        >
                          <div className="flex items-center gap-2">
                            <span>Edit Distribution</span>
                          </div>
                        </button>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </Transition>
              </div>
            )}
          </Menu>
        )}
      </div>
      {content}
    </div>
  );
};
